import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import Button, { ButtonTypes } from 'components/common/button';
import connect from 'components/lib/connect';
import DropdownMenu from 'components/common/dropdown_menu_v2';
import FilterGuidePerformance from 'actions/guide_performance/filter_guide_performance';
import InteractionType from 'models/interaction_type';
import NavigateTo from 'actions/current_location/navigate_to';
import PerformanceDateRange from 'models/performance_date_range';
import Reports from 'models/location/reports';

export function GuidePerformanceFilters({
  guideCollections,
  onChangeFilter,
  onOpenConversationReport,
  selectedDateRange,
  selectedGuideCollectionId,
  selectedInteractionType,
}) {
  const [dateRange, setDateRange] = useState(selectedDateRange || PerformanceDateRange.LAST_1_DAY);
  const [guideCollectionId, setGuideCollectionId] = useState(selectedGuideCollectionId || '');
  const [interactionType, setInteractionType] = useState(selectedInteractionType || '');

  const dateRangeText = dateRangeOptions.find(o => o.value === dateRange)?.label;
  const onChangeDateRangeFilter = useCallback(
    newDateRange => {
      onChangeFilter({ dateRange: newDateRange, guideCollectionId, interactionType });
      setDateRange(newDateRange);
    },
    [guideCollectionId, interactionType, onChangeFilter]
  );

  const guideCollectionText = guideCollections.find(o => o.value === guideCollectionId)?.label;
  const onChangeGuideCollectionFilter = useCallback(
    newGuideCollectionId => {
      onChangeFilter({ dateRange, guideCollectionId: newGuideCollectionId, interactionType });
      setGuideCollectionId(newGuideCollectionId);
    },
    [dateRange, interactionType, onChangeFilter]
  );

  const interactionTypeText = interactionTypeOptions.find(o => o.value === interactionType)?.label;
  const onChangeInteractionTypeFilter = useCallback(
    newInteractionType => {
      onChangeFilter({ dateRange, guideCollectionId, interactionType: newInteractionType });
      setInteractionType(newInteractionType);
    },
    [dateRange, guideCollectionId, onChangeFilter]
  );

  return (
    <ControlWrapper>
      <Filters>
        <StyledDropdownWrapper>
          <DropdownMenu
            className="date-range-filter"
            onSelect={onChangeDateRangeFilter}
            options={dateRangeOptions}
            text={dateRangeText}
          />
        </StyledDropdownWrapper>
        {!!guideCollections.length && (
          <StyledDropdownWrapper>
            <DropdownMenu
              className="guide-collection-filter"
              onSelect={onChangeGuideCollectionFilter}
              options={guideCollections}
              text={guideCollectionText}
            />
          </StyledDropdownWrapper>
        )}
        <StyledDropdownWrapper>
          <DropdownMenu
            className="interaction-type-filter"
            onSelect={onChangeInteractionTypeFilter}
            options={interactionTypeOptions}
            text={interactionTypeText}
          />
        </StyledDropdownWrapper>
      </Filters>
      <StyledButtonWrapper
        data-aid="answerPerformanceFilters-openConversationReport"
        onClick={onOpenConversationReport}
        type={ButtonTypes.SECONDARY}
      >
        Open Conversation Report
      </StyledButtonWrapper>
    </ControlWrapper>
  );
}

GuidePerformanceFilters.propTypes = {
  guideCollections: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })
  ),
  onChangeFilter: PropTypes.func,
  onOpenConversationReport: PropTypes.func,
  selectedDateRange: PropTypes.string,
  selectedGuideCollectionId: PropTypes.string,
  selectedInteractionType: PropTypes.string,
};

export const Filters = styled.div`
  display: flex;
  padding: ${p => p.theme.spacing.medium} 0;
`;

export const ControlWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

export const StyledButtonWrapper = styled(Button)`
  flex-shrink: 0;
  height: fit-content;
`;

const StyledDropdownWrapper = styled.div`
  margin-right: ${p => p.theme.spacing.inlineMedium};
`;

const dateRangeOptions = [
  { label: 'Last 1 day', value: PerformanceDateRange.LAST_1_DAY },
  { label: 'Last 7 days', value: PerformanceDateRange.LAST_7_DAYS },
  { label: 'Last 30 days', value: PerformanceDateRange.LAST_30_DAYS },
  { label: 'Last 90 days', value: PerformanceDateRange.LAST_90_DAYS },
];

const interactionTypeOptions = [
  { label: 'All Channels', value: '' },
  { label: 'Chat', value: InteractionType.CHAT },
  { label: 'Email', value: InteractionType.EMAIL },
  { label: 'Phone Call', value: InteractionType.PHONE_CALL },
  { label: 'SMS', value: InteractionType.SMS },
];

function mapStateToProps({ getProvider }) {
  const guideCollections = getProvider('guideCollections').findAll();
  const guideCollectionValues =
    guideCollections.length > 1
      ? guideCollections
          .map(guideCollection => ({
            value: guideCollection.id,
            label: guideCollection.name,
          }))
          .sort((a, b) => (a.label.toLowerCase() > b.label.toLowerCase() ? 1 : -1))
      : [];

  if (guideCollectionValues?.length) {
    guideCollectionValues.unshift({ label: 'All Guides', value: '' });
  }

  const location = getProvider('currentLocation').get();

  return {
    guideCollections: guideCollectionValues,
    selectedGuideCollectionId: location.guideCollectionId,
    selectedDateRange: location.dateRange,
    selectedInteractionType: location.interactionType,
  };
}

function mapExecuteToProps(executeAction) {
  return {
    onChangeFilter: ({ dateRange, guideCollectionId, interactionType }) => {
      executeAction(FilterGuidePerformance, { dateRange, guideCollectionId, interactionType });
    },
    onOpenConversationReport: () => {
      executeAction(NavigateTo, Reports.create({ slug: 'sidekick-guides-performance-detail' }));
    },
  };
}

const GuidePerformanceFiltersContainer = connect(mapStateToProps, mapExecuteToProps)(GuidePerformanceFilters);

export default GuidePerformanceFiltersContainer;
